import { Injectable } from '@angular/core';

import { UserMedcardGrpcContractService } from '@lyra/app/core/api/grpc/services/user-medcard.service';
import { ErrorService } from '@lyra/app/core/services/error.service';
import { MainApi } from '@mark/web-contracts';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, map, switchMap, withLatestFrom } from 'rxjs';

import { CHANGE_ROUTE } from '../actions/questionnaire.actions';
import { GET_QUESTIONS } from '../actions/questions.actions';
import * as actions from '../actions/woman-health.actions';
import { MedicalQuestionsSelectService } from '../selectors';
import { QuestionnaireSelectService } from '../selectors/questionnaire.selector';
import { QuestionsSelectService } from '../selectors/questions.selector';
import { SurveySelectService } from '../selectors/survey.selector';
import { WomanHealthSelectService } from '../selectors/woman-health.selector';
import { IQuestionnaireState } from '../states/questionnaire.state';

@Injectable()
export class WomanHealthEffects {
  setAnswer$ = createEffect(() =>
    this._actions$.pipe(
      ofType(actions.ANSWER_QUESTION_COMMAND),
      withLatestFrom(this._womanHealthSelectService.isLastQuestion()),
      map(([{ questionId, answer }, isLastQuestion]) =>
        actions.ANSWER_QUESTION_SUCCESS({ questionId, answer, isLastQuestion }),
      ),
    ),
  );

  answeredAllQuestions$ = createEffect(() =>
    this._actions$.pipe(
      ofType(actions.ANSWER_QUESTION_SUCCESS),
      filter((x) => !!x.isLastQuestion),
      map((x) => {
        let pregnancy: boolean = false;
        if (x.questionId === 'WomanHealth' || x.questionId === 'WomanHealthHRT') {
          if (x.answer.Type === MainApi.WomanHealthCondition.WomanHealthCondition_Pregnancy) {
            pregnancy = true;
          }
        }
        return actions.SEND_WOMAN_HEALTH_COMMAND({ pregnancy });
      }),
    ),
  );

  sendWomanHealth$ = createEffect(() =>
    this._actions$.pipe(
      ofType(actions.SEND_WOMAN_HEALTH_COMMAND),
      withLatestFrom(this._womanHealthSelectService.getWomanHealthCondition()),
      map(([{ pregnancy }, WomanHealthCondition]) => ({ pregnancy, WomanHealthCondition })),
      filter((x) => !!x.WomanHealthCondition),
      switchMap(({ pregnancy, WomanHealthCondition }) =>
        this._userMedcardGrpcContractService
          .updateWomanHealth(WomanHealthCondition!)
          .pipe(map(() => actions.SEND_WOMAN_HEALTH_SUCCESS({ pregnancy }))),
      ),
    ),
  );

  sendWomanHealthSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType(actions.SEND_WOMAN_HEALTH_SUCCESS),
      withLatestFrom(this._medicalQuestionsSelect.hasQuestions(), this._questionsSelect.hasQuestions()),
      map(([{ pregnancy }, hasMedicalQuestions, hasQuestions]) => {
        if (pregnancy) {
          return CHANGE_ROUTE({ route: 'pregnancy' });
        }
        if (!hasQuestions) {
          this._store$.dispatch(GET_QUESTIONS());
        }
        if (hasMedicalQuestions) {
          return CHANGE_ROUTE({ route: 'medical-questions' });
        } else {
          return CHANGE_ROUTE({ route: 'questions' });
        }
      }),
    ),
  );

  constructor(
    private _store$: Store<IQuestionnaireState>,
    private _actions$: Actions,
    private _womanHealthSelectService: WomanHealthSelectService,
    private _questionnaireSelect: QuestionnaireSelectService,
    private _userMedcardGrpcContractService: UserMedcardGrpcContractService,
    private _medicalQuestionsSelect: MedicalQuestionsSelectService,
    private _questionsSelect: QuestionsSelectService,
    private _surveySelect: SurveySelectService,
    private _errorService: ErrorService,
  ) {}
}
